import React, { useState, useMemo } from 'react'
import {
	Box,
	Typography,
	Button,
	Collapse,
	Autocomplete,
	TextField,
	Chip,
	useMediaQuery,
	useTheme
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckIcon from '@mui/icons-material/Check'
import PetsIcon from '@mui/icons-material/Pets'

// Pet Selection Card Component
const PetCard = ({ pet, index, isSelected, hasService, onClick }) => {
	return (
		<Box
			onClick={onClick}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '8px',
				padding: '8px 12px',
				bgcolor: isSelected ? '#F3E9FF' : '#FFFFFF',
				border: `1px solid ${isSelected ? '#9F7AEA' : '#CBD5E0'}`,
				borderRadius: '12px',
				cursor: 'pointer',
				transition: 'all 0.2s ease',
				flex: 1,
				'&:hover': {
					borderColor: '#9F7AEA',
					bgcolor: isSelected ? '#F3E9FF' : '#FAFAFA'
				}
			}}
		>
			{/* Pet Icon Placeholder */}
			<Box sx={{
				width: '30px',
				height: '30px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: '20px'
			}}>
				<PetsIcon />
			</Box>

			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '6px',
				flex: 1
			}}>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 500,
					fontSize: '18px',
					lineHeight: 1.21,
					color: '#000000'
				}}>
					{pet.name || `Pet ${index + 1}`}
				</Typography>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 500,
					fontSize: '12px',
					lineHeight: 1.21,
					color: hasService ? '#14AE5C' : '#A32CDE'
				}}>
					{hasService ? 'Service Selected ✓' : 'Select Service'}
				</Typography>
			</Box>
		</Box>
	)
}

// Service Card Component
const ServiceCard = ({ service, isSelected, onSelect, formatCurrency }) => {
	const [showDetails, setShowDetails] = useState(false)

	const handleCardClick = (e) => {
		// Don't select if clicking the "See details" button
		if (e.target.closest('.see-details-btn')) return
		onSelect(service)
	}

	return (
		<Box
			onClick={handleCardClick}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '6px',
				padding: '16px 0 12px 24px',
				bgcolor: isSelected ? '#F3E9FF' : '#FFFFFF',
				border: `1px solid ${isSelected ? '#9F7AEA' : '#CBD5E0'}`,
				borderRadius: '12px',
				transition: 'all 0.2s ease',
				boxShadow: isSelected ? '0px 16px 10px -15px rgba(0, 0, 0, 0.25)' : 'none',
				cursor: 'pointer',
				'&:hover': {
					borderColor: '#9F7AEA',
					bgcolor: isSelected ? '#F3E9FF' : '#FAFAFA'
				}
			}}
		>
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'flex-start',
			}}>
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '6px',
					alignItems: 'flex-start',
					flex: 1
				}}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 500,
						fontSize: '18px',
						lineHeight: 1.21,
						color: '#000000'
					}}>
						{service.name}
					</Typography>

					{/* See details / Hide details */}
					{service.description && (
						<button
							className="see-details-btn"
							onClick={(e) => {
								e.stopPropagation()
								setShowDetails(!showDetails)
							}}
							style={{
								p: 0,
								minWidth: 0,
								justifyContent: 'flex-start',
								color: '#A32CDE',
								fontFamily: 'Inter',
								fontWeight: 500,
								fontSize: '15px',
								lineHeight: 1.21,
								textTransform: 'none',
								'&:hover': {
									bgcolor: 'transparent',
									textDecoration: 'underline'
								}
							}}
						>
							{showDetails ? 'Hide details' : 'See details'}
						</button>
					)}
				</Box>

				{/* Price Badge */}
				<Box
					sx={{
						px: '14px',
						py: '6px',
						bgcolor: isSelected ? '#F4BEFF' : '#EFEFEF',
						color: '#000000',
						borderRadius: '8px 0 0 8px',
						border: isSelected ? '1px solid #9F7AEA' : 'none',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: 1.21
					}}
				>
					{formatCurrency(service.price)}
				</Box>
			</Box>

			{/* Description Collapse */}
			<Collapse in={showDetails}>
				<Box sx={{ pr: 3 }}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '14px',
						lineHeight: 1.21,
						color: '#A0AEC0'
					}}>
						{service.description}
					</Typography>
				</Box>
			</Collapse>
		</Box>
	)
}

// Selected Service Display Card (for add-ons step)
const SelectedServiceCard = ({ service, formatCurrency }) => {
	const [showDetails, setShowDetails] = useState(false)

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				padding: '16px 0 12px 24px',
				bgcolor: '#F3E9FF',
				border: '1px solid #9F7AEA',
				borderRadius: '12px'
			}}
		>
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'flex-start',
			}}>
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '6px',
					alignItems: 'flex-start',
					flex: 1
				}}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 500,
						fontSize: '18px',
						lineHeight: 1.21,
						color: '#000000'
					}}>
						{service.name}
					</Typography>

					{service.description && (
						<button
							onClick={() => setShowDetails(!showDetails)}
							style={{
								p: 0,
								minWidth: 0,
								justifyContent: 'flex-start',
								color: '#A32CDE',
								fontFamily: 'Inter',
								fontWeight: 500,
								fontSize: '15px',
								lineHeight: 1.21,
								textTransform: 'none',
								'&:hover': {
									bgcolor: 'transparent',
									textDecoration: 'underline'
								}
							}}
						>
							{showDetails ? 'Hide details' : 'See details'}
						</button>
					)}
				</Box>

				{/* Price Badge */}
				<Box
					sx={{
						px: '14px',
						py: '6px',
						bgcolor: '#F4BEFF',
						color: '#000000',
						borderRadius: '8px 0 0 8px',
						border: '1px solid #9F7AEA',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: 1.21
					}}
				>
					{formatCurrency(service.price)}
				</Box>
			</Box>

			<Collapse in={showDetails}>
				<Box sx={{ pr: 3 }}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '14px',
						lineHeight: 1.21,
						color: '#A0AEC0'
					}}>
						{service.description}
					</Typography>
				</Box>
			</Collapse>
		</Box>
	)
}

// Add-on Card Component
const AddOnCard = ({ addOn, isSelected, onToggle, formatCurrency, isMobile }) => {
	const [showDetails, setShowDetails] = useState(false)

	const handleCardClick = (e) => {
		// Don't toggle if clicking the "See details" button
		if (e.target.closest('.see-details-btn')) return
		onToggle(addOn)
	}

	return (
		<Box
			onClick={handleCardClick}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '6px',
				padding: '16px 0 12px 24px',
				bgcolor: isSelected ? '#F3E9FF' : '#FFFFFF',
				border: `1px solid ${isSelected ? '#9F7AEA' : '#CBD5E0'}`,
				borderRadius: '12px',
				transition: 'all 0.2s ease',
				boxShadow: isSelected ? '0px 16px 10px -15px rgba(0, 0, 0, 0.25)' : 'none',
				cursor: 'pointer',
				'&:hover': {
					borderColor: '#9F7AEA',
					bgcolor: isSelected ? '#F3E9FF' : '#FAFAFA'
				}
			}}
		>
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'flex-start',
			}}>
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '6px',
					alignItems: 'flex-start',
					flex: 1
				}}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						{isSelected && (
							<CheckIcon sx={{ color: '#9F7AEA', fontSize: '20px' }} />
						)}
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 500,
							fontSize: '18px',
							lineHeight: 1.21,
							color: '#000000'
						}}>
							{addOn.name}
						</Typography>
					</Box>

					{addOn.description && (
						<button
							className="see-details-btn"
							onClick={(e) => {
								e.stopPropagation()
								setShowDetails(!showDetails)
							}}
							style={{
								p: 0,
								minWidth: 0,
								height: '14px !important',
								maxHeight: '14px !important',
								justifyContent: 'flex-start',
								color: '#A32CDE',
								fontFamily: 'Inter',
								fontWeight: 500,
								fontSize: '15px',
								lineHeight: 1.21,
								textTransform: 'none',
								'&:hover': {
									bgcolor: 'transparent',
									textDecoration: 'underline'
								}
							}}
						>
							{showDetails ? 'Hide details' : 'See details'}
						</button>
					)}
				</Box>

				{/* Price Badge */}
				<Box
					sx={{
						px: '14px',
						py: '6px',
						bgcolor: isSelected ? '#F4BEFF' : '#EFEFEF',
						color: '#000000',
						borderRadius: '8px 0 0 8px',
						border: isSelected ? '1px solid #9F7AEA' : 'none',
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '14px',
						lineHeight: 1.21
					}}
				>
					{formatCurrency(addOn.price)}
				</Box>
			</Box>

			<Collapse in={showDetails}>
				<Box sx={{ pr: 3 }}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '14px',
						lineHeight: 1.21,
						color: '#A0AEC0'
					}}>
						{addOn.description}
					</Typography>
				</Box>
			</Collapse>
		</Box>
	)
}

// Main ServiceSelectionStep Component
function ServiceSelectionStep({
	pets,
	services,
	addOns,
	categories,
	petServices,
	onPetServicesChange,
	currentPetIndex = 0,
	onCurrentPetIndexChange,
	subStep = 'service', // 'service' | 'addons'
	onSubStepChange,
	currencySymbol = '$'
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const [selectedCategory, setSelectedCategory] = useState(null)

	const formatCurrency = (cents) => `${currencySymbol}${(cents / 100).toFixed(2)}`

	// Get current pet's service selection
	const currentPetService = petServices.find(ps => ps.petIndex === currentPetIndex)
	const selectedServiceId = currentPetService?.serviceId
	const selectedAddOnIds = currentPetService?.addOnIds || []

	// Get selected service object
	const selectedService = services.find(s => s.id === selectedServiceId)

	// Filter services by category_id
	const filteredServices = useMemo(() => {
		if (!selectedCategory) return services
		return services.filter(s => s.category_id === selectedCategory.id)
	}, [services, selectedCategory])

	// Filter add-ons by category_id
	const filteredAddOns = useMemo(() => {
		if (!selectedCategory) return addOns
		return addOns.filter(a => a.category_id === selectedCategory.id)
	}, [addOns, selectedCategory])

	// Handle service selection
	const handleServiceSelect = (service) => {
		const newPetServices = [...petServices]
		const existingIndex = newPetServices.findIndex(ps => ps.petIndex === currentPetIndex)

		if (existingIndex >= 0) {
			// Toggle off if same service selected
			if (newPetServices[existingIndex].serviceId === service.id) {
				newPetServices.splice(existingIndex, 1)
			} else {
				newPetServices[existingIndex] = {
					petIndex: currentPetIndex,
					serviceId: service.id,
					addOnIds: []
				}
				// Move to add-ons step
				if (onSubStepChange && addOns.length > 0) {
					onSubStepChange('addons')
				}
			}
		} else {
			newPetServices.push({
				petIndex: currentPetIndex,
				serviceId: service.id,
				addOnIds: []
			})
			// Move to add-ons step
			if (onSubStepChange && addOns.length > 0) {
				onSubStepChange('addons')
			}
		}

		onPetServicesChange(newPetServices)
	}

	// Handle add-on toggle
	const handleAddOnToggle = (addOn) => {
		const newPetServices = [...petServices]
		const existingIndex = newPetServices.findIndex(ps => ps.petIndex === currentPetIndex)

		if (existingIndex >= 0) {
			const currentAddOns = [...(newPetServices[existingIndex].addOnIds || [])]
			const addOnIndex = currentAddOns.indexOf(addOn.id)

			if (addOnIndex >= 0) {
				// Remove add-on
				currentAddOns.splice(addOnIndex, 1)
			} else {
				// Add add-on
				currentAddOns.push(addOn.id)
			}

			newPetServices[existingIndex] = {
				...newPetServices[existingIndex],
				addOnIds: currentAddOns
			}

			onPetServicesChange(newPetServices)
		}
	}

	// Check if pet has service selected
	const petHasService = (petIndex) => {
		return petServices.some(ps => ps.petIndex === petIndex && ps.serviceId)
	}

	// Category options for dropdown (categories are objects with id and name)
	const categoryOptions = [{ id: null, name: 'All categories' }, ...categories]

	// Get current pet name
	const currentPetName = pets[currentPetIndex]?.name || `Pet ${currentPetIndex + 1}`

	// Render Service Selection Sub-step
	const renderServiceSelection = () => (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '18px',
			width: '100%'
		}}>
			{/* Pet Selection Cards (for multiple pets) */}
			{pets.length > 1 && (
				<Box sx={{
					display: 'flex',
					gap: '10px',
					flexWrap: isMobile ? 'wrap' : 'nowrap'
				}}>
					{pets.map((pet, index) => (
						<PetCard
							key={index}
							pet={pet}
							index={index}
							isSelected={index === currentPetIndex}
							hasService={petHasService(index)}
							onClick={() => onCurrentPetIndexChange?.(index)}
						/>
					))}
				</Box>
			)}

			{/* Section Title */}
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: isMobile ? '24px' : '28px',
				lineHeight: 1.29,
				color: '#2D3748'
			}}>
				Select a service {pets.length > 1 ? `for ${currentPetName}` : ''}
			</Typography>

			{/* Category Filter */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '4px'
			}}>
				<Autocomplete
					options={categoryOptions}
					value={selectedCategory || categoryOptions[0]}
					onChange={(e, newValue) => {
						setSelectedCategory(newValue?.id ? newValue : null)
					}}
					getOptionLabel={(option) => option?.name || 'All categories'}
					isOptionEqualToValue={(option, value) => option?.id === value?.id}
					filterOptions={(options) => options}
					disableClearable
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder="All categories"
							sx={{
								'& .MuiOutlinedInput-root': {
									borderRadius: '6px',
									padding: '6px 10px',
									bgcolor: '#FFFFFF',
									'& fieldset': {
										borderColor: '#CBD5E0'
									},
									'&:hover fieldset': {
										borderColor: '#9F7AEA'
									},
									'&.Mui-focused fieldset': {
										borderColor: '#9F7AEA'
									}
								},
								'& .MuiInputBase-input': {
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '16px',
									lineHeight: 1.21,
									color: '#2D3748',
									padding: '12px 16px'
								}
							}}
						/>
					)}
					popupIcon={<KeyboardArrowDownIcon sx={{ color: '#A0AEC0' }} />}
				/>
			</Box>

			{/* Services List - Two columns on desktop, scrollable */}
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
				gap: '18px',
				maxHeight: isMobile ? 'none' : '300px',
				overflowY: isMobile ? 'visible' : 'auto',
				pr: isMobile ? 0 : 1 // Padding for scrollbar on desktop only
			}}>
				{filteredServices.map(service => (
					<ServiceCard
						key={service.id}
						service={service}
						isSelected={selectedServiceId === service.id}
						onSelect={handleServiceSelect}
						formatCurrency={formatCurrency}
					/>
				))}
			</Box>
		</Box>
	)

	// Render Add-ons Selection Sub-step
	const renderAddOnsSelection = () => (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '18px',
			width: '100%',
			height: isMobile ? '100%' : 'auto',
			minHeight: isMobile ? 0 : 'auto'
		}}>
			{/* Selected Service Card */}
			{selectedService && (
				<SelectedServiceCard
					service={selectedService}
					formatCurrency={formatCurrency}
				/>
			)}

			{/* Section Title */}
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: isMobile ? '24px' : '28px',
				lineHeight: 1.29,
				color: '#2D3748',
				flexShrink: 0
			}}>
				{'Popular add-ons'}
			</Typography>

			{/* Category Filter for Add-ons */}
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '4px',
				flexShrink: 0
			}}>
				<Autocomplete
					options={categoryOptions}
					value={selectedCategory || categoryOptions[0]}
					onChange={(e, newValue) => {
						setSelectedCategory(newValue?.id ? newValue : null)
					}}
					getOptionLabel={(option) => option?.name || 'All categories'}
					isOptionEqualToValue={(option, value) => option?.id === value?.id}
					filterOptions={(options) => options}
					disableClearable
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder="All categories"
							sx={{
								'& .MuiOutlinedInput-root': {
									borderRadius: '6px',
									padding: '6px 10px',
									bgcolor: '#FFFFFF',
									'& fieldset': {
										borderColor: '#CBD5E0'
									},
									'&:hover fieldset': {
										borderColor: '#9F7AEA'
									},
									'&.Mui-focused fieldset': {
										borderColor: '#9F7AEA'
									}
								},
								'& .MuiInputBase-input': {
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '16px',
									lineHeight: 1.21,
									color: '#2D3748',
									padding: '12px 16px'
								}
							}}
						/>
					)}
					popupIcon={<KeyboardArrowDownIcon sx={{ color: '#A0AEC0' }} />}
				/>
			</Box>

			{/* Add-ons List - Two columns on desktop, scrollable on mobile */}
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
				gap: '18px',
				maxHeight: isMobile ? 'none' : '300px',
				overflowY: isMobile ? 'visible' : 'auto',
				pr: isMobile ? 0 : 1 // Padding for scrollbar on desktop only
			}}>
				{filteredAddOns.map(addOn => (
					<AddOnCard
						key={addOn.id}
						addOn={addOn}
						isSelected={selectedAddOnIds.includes(addOn.id)}
						onToggle={handleAddOnToggle}
						formatCurrency={formatCurrency}
						isMobile={isMobile}
					/>
				))}
			</Box>
			{/* Selected Add-ons Summary - Fixed at bottom on mobile */}
			<Box sx={{
				display: 'flex',
				flexWrap: 'wrap',
				gap: '8px',
				pb: isMobile ? '100px' : '0px',
				pt: 1,
				borderTop: '1px dashed #CBD5E0',
				flexShrink: 0,
				minHeight: '40px',
				bgcolor: isMobile ? '#F9FAFB' : 'transparent'
			}}>
				{selectedAddOnIds.length > 0 ? (
					<>
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 500,
							fontSize: '14px',
							color: '#4A5568',
							alignSelf: 'center'
						}}>
							Selected:
						</Typography>
						{selectedAddOnIds.map(addOnId => {
							const addOn = addOns.find(a => a.id === addOnId)
							return addOn ? (
								<Chip
									key={addOnId}
									label={addOn.name}
									size="small"
									sx={{
										bgcolor: '#E9D8FD',
										color: '#6B46C1',
										fontFamily: 'Inter',
										fontWeight: 500
									}}
								/>
							) : null
						})}
					</>
				) : (
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '14px',
						color: '#A0AEC0',
						alignSelf: 'center'
					}}>
						No add-ons selected
					</Typography>
				)}
			</Box>
		</Box>
	)

	// Render based on sub-step
	if (subStep === 'addons' && selectedServiceId && addOns.length > 0) {
		return renderAddOnsSelection()
	}

	return renderServiceSelection()
}

export default ServiceSelectionStep
